import { useRef, type KeyboardEvent } from "react";

/**
 * Arrow-key roving tabindex for a same-page radiogroup built from plain `<button
 * role="radio">` elements. The native ARIA radiogroup keyboard contract is one tab stop
 * for the whole group — Left/Right (and Up/Down) move focus *and* the selection — not N
 * independently-tabbable buttons where arrows do nothing. `role="radio"` without this
 * hook promises that contract and does not implement it: a screen-reader user hears
 * "radio, 1 of 2", reaches for arrow keys, and gets silence.
 *
 * Disabled options are skipped when computing the next index, so the roving stop never
 * lands where Tab and Enter could not follow it anyway.
 */
export function useRovingRadioGroup<T extends string>(
  options: readonly T[],
  value: T,
  onChange: (next: T) => void,
  disabled = false,
) {
  const buttonRefs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});

  const registerRef = (option: T) => (element: HTMLButtonElement | null) => {
    buttonRefs.current[option] = element;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const currentIndex = options.indexOf(value);
    let step = 0;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") step = 1;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") step = -1;
    else if (event.key === "Home") step = -currentIndex;
    else if (event.key === "End") step = options.length - 1 - currentIndex;
    else return;

    event.preventDefault();
    const nextIndex = (currentIndex + step + options.length) % options.length;
    const next = options[nextIndex];
    onChange(next);
    buttonRefs.current[next]?.focus();
  };

  const radioProps = (option: T) => ({
    ref: registerRef(option),
    tabIndex: option === value ? 0 : -1,
    onKeyDown: handleKeyDown,
  });

  return { radioProps };
}
